import { useTranslation } from "react-i18next";
import { PlatformRole } from "~/config/types";
import { useLanguageContext } from "~/context/language";
import { useAuthContext } from "~/hooks/useAuthContext";
import { useUserContext } from "~/hooks/useUserContext";
import { deleteSession } from "~/api/v2/requests";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "~/atoms/dropdown-menu";
import { Button } from "~/atoms/button";
import AvatarWithFallback from "~/molecules/AvatarWithFallback";
import { printFullName } from "~/helpers/user";
import {
  Languages,
  LogOut,
  Settings,
  Users,
  Share,
  HelpCircle,
} from "lucide-react";
import { languages, getLangLabels } from "~/i18n";
import { Link } from "react-router";
import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";
import { featureFlags } from "~/config/featureFlags";

const UserNav = () => {
  const { t } = useTranslation();
  const { user } = useUserContext();

  const langLabel = getLangLabels(t);
  const { language, setLanguage } = useLanguageContext();
  const { setUser } = useAuthContext();
  const posthog = usePostHog();
  const isSpMirroringEnabled = useFeatureFlagEnabled(featureFlags.spMirroring);

  const isReader = user.role === PlatformRole.reader;
  const isSuperAdmin = user.role === PlatformRole.superAdmin;

  const signOut = async () => {
    await deleteSession();
    setUser(undefined);
    posthog.reset();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-10 w-10 rounded-full">
          <AvatarWithFallback user={user} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm leading-none font-medium">
              {printFullName(user)}
            </p>
            <p className="text-muted-foreground text-xs leading-none">
              {user.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <Link to="/account-settings">
            <DropdownMenuItem>
              <Settings />
              {t(($) => $.settings.header)}
            </DropdownMenuItem>
          </Link>
          <Link to="/users" hidden={isReader}>
            <DropdownMenuItem>
              <Users />
              {t(($) => $.commons.users)}
            </DropdownMenuItem>
          </Link>
          <Link
            to="/onboarding/sharepoint"
            hidden={!isSuperAdmin || !isSpMirroringEnabled}
          >
            <DropdownMenuItem>
              <Share />
              {t(($) => $.sharepoint.title)}
            </DropdownMenuItem>
          </Link>
          <Link to="/support">
            <DropdownMenuItem>
              <HelpCircle />
              {t(($) => $.commons.help)}
            </DropdownMenuItem>
          </Link>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Languages />
            {t(($) => $.commons.language)}
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup
                value={language}
                onValueChange={setLanguage}
              >
                {Object.values(languages).map((lang) => (
                  <DropdownMenuRadioItem key={lang.code} value={lang.code}>
                    {langLabel[lang.code]}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={signOut}>
          <LogOut />
          {t(($) => $.commons.signOut)}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserNav;
